import { useEffect } from 'react'
import { Alert, Form, Input, Modal, Select } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  createDistrict,
  createRegion,
  updateDistrict,
  updateRegion,
  type District,
  type Region,
} from './geoApi'
import { useRegionOptions } from './useGeoOptions'

export type GeoKind = 'region' | 'district'

interface GeoFormModalProps {
  open: boolean
  kind: GeoKind
  /** `null` — yangi yozuv */
  entry: Region | District | null
  /** Yangi tuman uchun oldindan tanlangan viloyat */
  defaultRegionId?: string | null
  onClose: () => void
}

interface FormValues {
  regionId?: string
  name: string
  code?: string
}

const NAME_MAX = 200
const CODE_MAX = 32

export function GeoFormModal({
  open,
  kind,
  entry,
  defaultRegionId = null,
  onClose,
}: GeoFormModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()
  const isDistrict = kind === 'district'
  const regions = useRegionOptions(true, open && isDistrict)

  useEffect(() => {
    if (!open) return
    form.setFieldsValue({
      name: entry?.name ?? '',
      code: entry?.code ?? '',
      regionId: (entry && 'regionId' in entry ? entry.regionId : defaultRegionId) ?? undefined,
    })
  }, [open, entry, defaultRegionId, form])

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      const name = values.name.trim()
      const code = values.code?.trim() || null
      if (isDistrict) {
        const regionId = values.regionId ?? ''
        return entry
          ? updateDistrict(entry.id, { regionId, name, code, active: entry.active })
          : createDistrict({ regionId, name, code })
      }
      return entry
        ? updateRegion(entry.id, { name, code, active: entry.active })
        : createRegion({ name, code })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.geo.all })
      form.resetFields()
      onClose()
    },
  })

  const close = () => {
    save.reset()
    onClose()
  }

  const titleKey = entry
    ? isDistrict
      ? 'geo.districts.editTitle'
      : 'geo.regions.editTitle'
    : isDistrict
      ? 'geo.districts.createTitle'
      : 'geo.regions.createTitle'

  return (
    <Modal
      open={open}
      title={t(titleKey)}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      confirmLoading={save.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={520}
    >
      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        {isDistrict ? (
          <Form.Item
            name="regionId"
            label={t('geo.region')}
            rules={[{ required: true, message: t('geo.validation.regionRequired') }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              loading={regions.isPending}
              placeholder={t('geo.regionPlaceholder')}
              options={regions.options.map((r) => ({ value: r.id, label: r.name }))}
            />
          </Form.Item>
        ) : null}

        <Form.Item
          name="name"
          label={t('geo.name')}
          rules={[
            { required: true, message: t('geo.validation.nameRequired') },
            { max: NAME_MAX, message: t('geo.validation.nameLength', { max: NAME_MAX }) },
          ]}
        >
          <Input autoFocus autoComplete="off" />
        </Form.Item>

        <Form.Item
          name="code"
          label={t('geo.code')}
          extra={t('geo.codeHint')}
          rules={[{ max: CODE_MAX, message: t('geo.validation.codeLength', { max: CODE_MAX }) }]}
        >
          <Input autoComplete="off" />
        </Form.Item>
      </Form>
    </Modal>
  )
}
